import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Send, CheckCircle2, ArrowRight, Lock, User } from 'lucide-react';
import clsx from 'clsx';
import { useAuth } from '@/lib/auth';
import type { Papel } from '@/lib/types';

type Aba = 'magic' | 'senha';
type MagicState =
  | { kind: 'idle' }
  | { kind: 'sending' }
  | { kind: 'sent'; devMagicUrl?: string }
  | { kind: 'error'; message: string };
type SenhaState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'error'; message: string };

export function Login() {
  const { user, requestMagicLink, loginUsuario } = useAuth();
  const navigate = useNavigate();
  const [aba, setAba] = useState<Aba>('magic');
  const [email, setEmail] = useState('');
  const [usuario, setUsuario] = useState('');
  const [senha, setSenha] = useState('');
  const [magic, setMagic] = useState<MagicState>({ kind: 'idle' });
  const [senhaState, setSenhaState] = useState<SenhaState>({ kind: 'idle' });

  useEffect(() => {
    if (user) navigate(defaultRoute(user.papel), { replace: true });
  }, [user, navigate]);

  async function onMagic(e: FormEvent) {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      setMagic({ kind: 'error', message: 'Digite um email válido.' });
      return;
    }
    setMagic({ kind: 'sending' });
    try {
      const { devMagicUrl } = await requestMagicLink(email);
      setMagic({ kind: 'sent', devMagicUrl });
    } catch (err) {
      setMagic({ kind: 'error', message: err instanceof Error ? err.message : 'Erro ao enviar email.' });
    }
  }

  async function onSenha(e: FormEvent) {
    e.preventDefault();
    if (!usuario.trim() || !senha) {
      setSenhaState({ kind: 'error', message: 'Preencha usuário e senha.' });
      return;
    }
    setSenhaState({ kind: 'submitting' });
    try {
      const u = await loginUsuario(usuario.trim(), senha);
      navigate(defaultRoute(u.papel), { replace: true });
    } catch (err) {
      setSenhaState({
        kind: 'error',
        message: err instanceof Error ? err.message : 'Usuário ou senha incorretos.',
      });
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-bg via-blue-dark/30 to-bg">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue to-blue-glow flex items-center justify-center text-white font-extrabold shadow-xl shadow-blue/30 text-lg">
              AP
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-1">Apex Research</h1>
          <p className="text-muted text-sm">Gestão de Demandas Audiovisuais</p>
        </div>

        <div className="bg-panel border border-border rounded-2xl p-8 shadow-2xl shadow-bg/50">
          {magic.kind !== 'sent' && (
            <div className="flex items-center bg-panel-2 border border-border rounded-lg p-1 mb-6">
              <TabBtn active={aba === 'magic'} onClick={() => setAba('magic')}>
                <Mail className="w-3.5 h-3.5" /> Link por email
              </TabBtn>
              <TabBtn active={aba === 'senha'} onClick={() => setAba('senha')}>
                <Lock className="w-3.5 h-3.5" /> Usuário e senha
              </TabBtn>
            </div>
          )}

          {magic.kind === 'sent' ? (
            <div className="text-center">
              <div className="inline-flex w-14 h-14 rounded-full bg-green/15 items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-green" />
              </div>
              <h2 className="text-lg font-semibold mb-2">Verifica seu email</h2>
              <p className="text-sm text-muted mb-1">
                Se <strong className="text-text">{email}</strong> estiver cadastrado, você vai receber um link em alguns segundos.
              </p>
              <p className="text-xs text-muted mt-4">
                O link expira em 15 minutos.<br />Não chegou? Confere o spam ou tenta de novo.
              </p>
              {magic.devMagicUrl && (
                <div className="mt-6 bg-orange/10 border border-orange/30 rounded-lg p-3 text-left">
                  <div className="text-[10px] uppercase tracking-wide text-orange font-bold mb-1">Modo dev · sem Resend configurado</div>
                  <a href={magic.devMagicUrl} className="text-xs text-blue-light underline break-all">
                    {magic.devMagicUrl}
                  </a>
                </div>
              )}
              <button
                onClick={() => setMagic({ kind: 'idle' })}
                className="text-xs text-muted hover:text-text mt-6 underline"
              >
                Tentar com outro email
              </button>
            </div>
          ) : aba === 'magic' ? (
            <>
              <h2 className="text-lg font-semibold mb-1 text-center">Entrar via link</h2>
              <p className="text-sm text-muted text-center mb-6">
                Recebe um link no seu email pra entrar sem senha
              </p>
              <form onSubmit={onMagic} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted mb-2">Seu email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                      type="email" autoFocus autoComplete="email"
                      placeholder="voce@apexpartners.com.br"
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      disabled={magic.kind === 'sending'}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
                <button type="submit" disabled={magic.kind === 'sending' || !email}
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  {magic.kind === 'sending' ? 'Enviando…' : (
                    <><Send className="w-4 h-4" />Receber link de acesso<ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
                {magic.kind === 'error' && (
                  <div className="bg-red/15 border border-red/30 text-red text-xs rounded-lg p-3">{magic.message}</div>
                )}
              </form>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold mb-1 text-center">Entrar com usuário</h2>
              <p className="text-sm text-muted text-center mb-6">
                Use as credenciais enviadas no email de boas-vindas
              </p>
              <form onSubmit={onSenha} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted mb-2">Usuário</label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                      type="text" autoFocus autoComplete="username"
                      placeholder="nome.cliente"
                      value={usuario} onChange={(e) => setUsuario(e.target.value)}
                      disabled={senhaState.kind === 'submitting'}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold uppercase tracking-wide text-muted mb-2">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
                    <input
                      type="password" autoComplete="current-password"
                      placeholder="••••••••"
                      value={senha} onChange={(e) => setSenha(e.target.value)}
                      disabled={senhaState.kind === 'submitting'}
                      className="input w-full pl-10"
                    />
                  </div>
                </div>
                <button type="submit" disabled={senhaState.kind === 'submitting' || !usuario || !senha}
                  className="btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
                  {senhaState.kind === 'submitting' ? 'Entrando…' : (
                    <>Entrar<ArrowRight className="w-4 h-4" /></>
                  )}
                </button>
                {senhaState.kind === 'error' && (
                  <div className="bg-red/15 border border-red/30 text-red text-xs rounded-lg p-3">{senhaState.message}</div>
                )}
              </form>
            </>
          )}
        </div>

        <div className="text-center mt-6 text-xs text-muted">
          Não tem acesso? Solicite à <strong>Coordenação Produtora</strong>.
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button" onClick={onClick}
      className={clsx(
        'flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded transition-colors',
        active
          ? 'bg-blue text-white shadow-sm shadow-blue/30'
          : 'text-text-2 hover:text-text',
      )}
    >
      {children}
    </button>
  );
}

function defaultRoute(papel: Papel): string {
  switch (papel) {
    case 'Diretoria':
    case 'Comercial':
      return '/dash-comercial';
    case 'Cliente':
      return '/portal';
    default:
      return '/demandas';
  }
}
